import {
	Button,
	Checkbox,
	Chip,
	EmptyState,
	Input,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useTopUp } from "~/common/billing/topup";
import { useAccess, whyDisabled } from "~/core/access/index";
import { ApiError, api, type GapRow } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Stat } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { Drill, DrillLink } from "~/modules/analytics/components/detail";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { matches, TableTools } from "~/modules/analytics/components/table";
import { trackGeneration } from "~/modules/analytics/lib/generation-tracking";
import { readingPath } from "~/modules/analytics/lib/loop";

/**
 * The gap list: the product's actual output.
 *
 * No price on the button, because there is not one to put there: a page costs
 * what its models charge, which is not known until they have answered. What the
 * screen can show — and does, beside every control that spends — is the balance
 * they will come out of, and it stops at zero rather than at a quote.
 */
export function GapsPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const topUp = useTopUp();
	const { locale } = useI18n();
	const { editor, why } = useAccess();
	const [adding, setAdding] = useState(false);
	const [gapText, setGapText] = useState("");
	const [saving, setSaving] = useState(false);
	const queryClient = useQueryClient();
	const refresh = () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) });
	const add = useMutation({
		mutationFn: (text: string) => api.addGap(entityId, text, filters.locale),
		onSuccess: refresh,
	});
	const fromGap = useMutation({ mutationFn: (gapId: string) => api.generateFromGap(gapId), onSuccess: refresh });
	async function addGap(event: React.FormEvent) {
		event.preventDefault();
		setSaving(true);
		try {
			await add.mutateAsync(gapText);
			setGapText("");
			setAdding(false);
			toast.success(t("common.saved"));
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setSaving(false);
		}
	}
	const [busy, setBusy] = useState<string | null>(null);
	const [query, setQuery] = useState("");
	const [picked, setPicked] = useState<Set<string>>(new Set());

	const state = useQuery({
		queryKey: [
			...keys.entity(entityId),
			"gaps",
			{ range: filters.range, engine: filters.engine, locale: filters.locale, topics: filters.topics },
		],
		queryFn: () => api.gaps(entityId, filters),
		...read,
	});

	// The batch as one write, so the brand is read again once at the end
	// rather than after every page it queued.
	const batch = useMutation({
		mutationFn: async (ids: string[]) => {
			let done = 0;
			const failures: string[] = [];
			for (const id of ids) {
				try {
					trackGeneration((await api.generateFromGap(id)).job_id);
					done += 1;
				} catch (e) {
					failures.push(e instanceof ApiError ? (e.detail ?? e.localized(locale)) : String(e));
				}
			}
			return { done, failures };
		},
		onSettled: refresh,
	});

	function toggle(gapId: string) {
		setPicked((current) => {
			const next = new Set(current);
			if (!next.delete(gapId)) next.add(gapId);
			return next;
		});
	}

	/**
	 * Put a gap back on the list when the generation that claimed it is gone.
	 *
	 * Offered only where the row already says the generation broke off, so the
	 * server's own guard — it refuses a gap whose job is still alive — should
	 * never be what stops this. The button exists because nothing else does:
	 * the weekly loop hands back a gap whose draft failed in front of it, and a
	 * gap left behind by a closed gate or a withdrawn run has nobody watching.
	 */
	async function release(gapId: string) {
		setBusy(gapId);
		try {
			await api.releaseGap(gapId);
			refresh();
			toast.success(t("gaps.released"));
		} catch (e) {
			toast.error(e instanceof ApiError ? (e.detail ?? e.localized(locale)) : String(e));
		} finally {
			setBusy(null);
		}
	}

	async function generate(gapId: string) {
		setBusy(gapId);
		try {
			trackGeneration((await fromGap.mutateAsync(gapId)).job_id);
			toast.success(t("gaps.queued"));
		} catch (e) {
			// The refusal reason is the useful part: "Coda and Notion share only 2
			// datapoints" tells the customer what to do next — so it stays on
			// screen until dismissed rather than fading like a confirmation.
			toast.error(e instanceof ApiError ? (e.detail ?? e.localized(locale)) : String(e), { duration: Infinity });
		} finally {
			setBusy(null);
		}
	}

	/**
	 * The same call, once per selected row, in series.
	 *
	 * Not in parallel: each one takes a credit under a row lock, and firing ten
	 * at a balance that covers eight would have eight succeed and two fail in an
	 * order nobody chose. In series the customer gets the first eight they
	 * picked and a refusal that names the reason.
	 */
	async function generateAll(ids: string[]) {
		setBusy("batch");
		const { done, failures } = await batch.mutateAsync(ids);
		for (const failure of failures.slice(0, 3)) toast.error(failure, { duration: Infinity });
		toast.success(
			failures.length === 0 ? t("gaps.batchDone", { done }) : t("gaps.batchPartial", { done, failed: failures.length }),
		);
		setPicked(new Set());
		setBusy(null);
	}

	return (
		<StageShell
			entityId={entityId}
			reading="gaps"
			lede={t("gaps.lede")}
			filters={filters}
			setFilters={setFilters}
			actions={
				<Chip
					variant={adding ? undefined : "primary"}
					leftIcon={Plus}
					type="button"
					disabled={!editor}
					title={whyDisabled(editor, why.editor)}
					onClick={() => setAdding(!adding)}
				>
					{adding ? t("common.cancel") : t("manual.addGap")}
				</Chip>
			}
		>
			{adding ? (
				<form className="panel" onSubmit={addGap} style={{ marginBlock: "var(--s-6)" }}>
					<fieldset
						className="bare-fieldset"
						disabled={!editor || saving}
						style={{ display: "grid", gap: "var(--s-4)" }}
					>
						<p className="muted">{t("manual.gapHelp")}</p>
						<label htmlFor="manual-gap-question">
							{t("manual.gapQuestion")}
							<Input
								id="manual-gap-question"
								value={gapText}
								onChange={(event) => setGapText(event.target.value)}
								minLength={8}
								maxLength={300}
								required
							/>
						</label>
						<div className="row-actions">
							<Button type="submit">{t("common.save")}</Button>
						</div>
					</fieldset>
				</form>
			) : null}
			<Async state={state}>
				{(data) => {
					// Open alone: the list now also carries the ones already acted on,
					// so that a gap does not disappear the moment somebody uses it.
					const open = data.gaps.filter((gap) => gap.status === "open");
					const rows = data.gaps.filter((gap) => matches(query, gap.question, ...gap.competitor_domains));
					const selectable = rows.filter((gap) => gap.status === "open");
					const selected = rows.filter((gap) => picked.has(gap.id) && gap.status === "open");
					const affordable = data.balance > 0;

					return (
						<>
							<div className="stats insight-stats">
								<Stat label={t("gaps.balanceLabel")} hint={t("hint.balance")} value={String(data.balance)} />
								<Stat label={t("gaps.openLabel")} hint={t("hint.openGaps")} value={String(open.length)} />
							</div>

							{data.gaps.length === 0 ? (
								<EmptyState size="compact" title={t("gaps.empty")} />
							) : (
								<>
									<TableTools
										actions={
											selected.length > 0 ? (
												<>
													<Button
														variant="default"
														type="button"
														disabled={busy !== null || !editor || !affordable}
														title={whyDisabled(editor, why.editor)}
														onClick={() => generateAll(selected.map((gap) => gap.id))}
													>
														{t("gaps.generateSelected", { n: selected.length })}
													</Button>
													{affordable ? null : (
														<>
															<span className="faint">{t("gaps.noCredits")}</span>
															<Button variant="link" type="button" onClick={() => topUp("credits")}>
																{t("topup.open")}
															</Button>
														</>
													)}
												</>
											) : null
										}
										query={query}
										onQuery={setQuery}
										placeholder={t("visibility.question")}
										shown={rows.length}
										total={data.gaps.length}
										csv={{ name: "gaps", rows: csv(rows) }}
									></TableTools>

									{rows.length === 0 ? (
										<EmptyState size="compact" title={t("table.noMatch")} />
									) : (
										<div className="table-scroll">
											<Table variant="resource">
												<TableHeader>
													<TableRow>
														<TableHead>
															<Checkbox
																aria-label={t("gaps.selectAll")}
																disabled={!editor || busy !== null || selectable.length === 0}
																checked={
																	selected.length === 0
																		? false
																		: selected.length === selectable.length
																			? true
																			: "indeterminate"
																}
																onCheckedChange={(checked) =>
																	setPicked(
																		checked === true ? new Set(selectable.map((gap) => gap.id)) : new Set<string>(),
																	)
																}
															/>
														</TableHead>
														<TableHead className="wrap">{t("visibility.question")}</TableHead>
														<TableHead>{t("gaps.competitors")}</TableHead>
														<TableHead>{t("gaps.suggested")}</TableHead>
														<TableHead>{t("gaps.became")}</TableHead>
														<TableHead />
													</TableRow>
												</TableHeader>
												<TableBody>
													{rows.map((gap) => (
														<TableRow key={gap.id}>
															<TableCell>
																{gap.status === "open" ? (
																	<Checkbox
																		aria-label={t("gaps.select")}
																		checked={picked.has(gap.id)}
																		onCheckedChange={() => toggle(gap.id)}
																	/>
																) : null}
															</TableCell>
															<TableCell className="wrap">
																{/* A gap is "they were cited here and you were not",
																    and the evidence for it is the answers on that
																    question in that week. */}
																{gap.engine === "manual" ? (
																	gap.question
																) : (
																	<Drill
																		view={{
																			kind: "answers",
																			label: gap.question,
																			filters: { question: gap.question_id, engine: gap.engine, week: gap.week },
																		}}
																	>
																		{gap.question}
																	</Drill>
																)}
																<div className="faint">
																	{gap.engine === "manual" ? t("manual.gapSource") : gap.engine} · {gap.week}
																</div>
															</TableCell>
															<TableCell className="wrap">
																{gap.competitor_domains.slice(0, 3).map((domain) => (
																	<Drill className="pill" view={{ kind: "rival", label: domain, domain }} key={domain}>
																		{domain}
																	</Drill>
																))}
															</TableCell>
															<TableCell>
																<span className="pill pill-accent">{gap.suggested_kind}</span>
															</TableCell>
															{/* What became of it. The link is the whole reason
															    this column exists: a gap that had been generated
															    used to vanish from the list, so "what happened to
															    the ones I paid for" had no answer anywhere. */}
															<TableCell className="wrap">
																{gap.page_id ? (
																	<DrillLink to={`/w/${entityId}/pages/${gap.page_id}`}>{t("gaps.draft")}</DrillLink>
																) : gap.status !== "queued" ? (
																	<span className="faint">—</span>
																) : gap.generating ? (
																	<span className="faint">{t("gaps.queued")}</span>
																) : (
																	// A state that changes what the row's action is,
																	// so it is named rather than left reading like the
																	// one it is not: nothing is working on this.
																	<span className="pill pill-warning">{t("gaps.stalled")}</span>
																)}
															</TableCell>
															<TableCell className="num">
																<Button asChild variant="ghost" size="sm">
																	<Link
																		to={`${readingPath(`/w/${entityId}`, "plans")}?${new URLSearchParams({ new: "1", gap: gap.id, title: gap.question })}`}
																	>
																		{t("plans.new")}
																	</Link>
																</Button>
																{gap.status === "queued" && !gap.generating && (
																	<Button
																		variant="default"
																		type="button"
																		disabled={busy !== null || !editor}
																		title={whyDisabled(editor, why.editor)}
																		onClick={() => release(gap.id)}
																	>
																		{t("gaps.release")}
																	</Button>
																)}
																{gap.status !== "open" ? null : (
																	// Quiet, not filled. Fifty filled buttons down a
																	// table is fifty primary actions, which is none —
																	// the one filled control on this screen is the
																	// batch, and these are the way to do one of them.
																	<Button
																		variant="default"
																		type="button"
																		disabled={busy !== null || !editor || data.balance <= 0}
																		title={whyDisabled(editor, why.editor)}
																		onClick={() => generate(gap.id)}
																	>
																		{t("gaps.generate")}
																	</Button>
																)}
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</div>
									)}
								</>
							)}
						</>
					);
				}}
			</Async>
		</StageShell>
	);
}

function csv(rows: GapRow[]): (string | number)[][] {
	return [
		["question", "engine", "week", "cited_instead", "suggested_page", "status"],
		...rows.map((gap) => [
			gap.question,
			gap.engine,
			gap.week,
			gap.competitor_domains.join(" "),
			gap.suggested_kind,
			gap.status,
		]),
	];
}
